<template>
    <v-card variant="tonal" color="primary" rounded="lg" class="mb-4 feed-summary-card" elevation="0">
        <v-card-text class="pa-4">
            <div class="text-caption font-weight-bold text-uppercase text-medium-emphasis mb-3"
                 style="letter-spacing: 0.08em;">
                Last 7 days
            </div>
            <div class="d-flex ga-4 flex-wrap">
                <div class="stat-block">
                    <div class="d-flex align-center ga-1">
                        <span class="text-h5 font-weight-bold">{{ tripCount }}</span>
                        <v-icon v-if="tripTrend" :icon="tripTrend.icon" :color="tripTrend.color"
                                :title="tripTrend.title" size="16"/>
                    </div>
                    <div class="text-caption text-medium-emphasis">Trips</div>
                </div>
                <v-divider vertical class="mx-1"/>
                <div class="stat-block">
                    <div class="d-flex align-center ga-1">
                        <span class="text-h5 font-weight-bold">{{ totalDistanceText }}</span>
                        <v-icon v-if="distanceTrend" :icon="distanceTrend.icon" :color="distanceTrend.color"
                                :title="distanceTrend.title" size="16"/>
                    </div>
                    <div class="text-caption text-medium-emphasis">Distance</div>
                </div>
                <v-divider vertical class="mx-1"/>
                <div class="stat-block">
                    <div class="d-flex align-center ga-1">
                        <span class="text-h5 font-weight-bold">{{ alertCount }}</span>
                        <v-icon v-if="alertTrend" :icon="alertTrend.icon" :color="alertTrend.color"
                                :title="alertTrend.title" size="16"/>
                    </div>
                    <div class="text-caption text-medium-emphasis">Alerts</div>
                </div>
                <v-divider v-if="topDriver" vertical class="mx-1"/>
                <div v-if="topDriver" class="stat-block">
                    <div class="text-body-1 font-weight-bold text-truncate" style="max-width: 140px;">
                        {{ topDriver }}
                    </div>
                    <div class="text-caption text-medium-emphasis">Top driver</div>
                </div>
            </div>
        </v-card-text>
    </v-card>
</template>

<script setup>
import {computed} from 'vue';
import {strOperations} from "@/composables/globals";
import {parseApiDate} from "@/composables/useTimezone";

const props = defineProps({
    trips: {type: Array, default: () => []},
    alerts: {type: Array, default: () => []},
});

// Two equal rolling windows: the 7 x 24 hours ending now and the 7 x 24 hours before.
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const nowMs = Date.now();
const inWindow = (ts, weeksBack) => {
    const ms = parseApiDate(ts)?.getTime();
    if (!ms) return false;
    const end = nowMs - weeksBack * WEEK_MS;
    return ms <= end && ms > end - WEEK_MS;
};

const recentTrips = computed(() => props.trips.filter(t => inWindow(t.time, 0)));
const recentAlerts = computed(() => props.alerts.filter(a => inWindow(a.event_timestamp, 0)));
const previousTrips = computed(() => props.trips.filter(t => inWindow(t.time, 1)));
const previousAlerts = computed(() => props.alerts.filter(a => inWindow(a.event_timestamp, 1)));

const tripCount = computed(() => recentTrips.value.length);

const thisWeekDistanceKm = computed(() =>
    recentTrips.value.reduce((sum, t) => sum + (parseFloat(t.distance) || 0), 0)
);
const prevWeekDistanceKm = computed(() =>
    previousTrips.value.reduce((sum, t) => sum + (parseFloat(t.distance) || 0), 0)
);

const totalDistanceText = computed(() => {
    if (!thisWeekDistanceKm.value) return '0';
    return strOperations().unitifyDistance(thisWeekDistanceKm.value, 'distance', 'km');
});

const alertCount = computed(() => recentAlerts.value.length);

const topDriver = computed(() => {
    const counts = {};
    for (const t of recentTrips.value) {
        if (t.driver_name) counts[t.driver_name] = (counts[t.driver_name] || 0) + 1;
    }
    const entries = Object.entries(counts);
    if (!entries.length) return null;
    return entries.sort((a, b) => b[1] - a[1])[0][0];
});

const trendFor = (cur, prev) => {
    if (!prev || prev === 0) return null;
    if (cur === prev) return {icon: 'mdi-minus', color: 'grey', title: 'Same as last week'};
    const up = cur > prev;
    return {icon: up ? 'mdi-arrow-up' : 'mdi-arrow-down', color: 'grey', title: `${up ? 'Up' : 'Down'} vs last week`};
};
const tripTrend = computed(() => trendFor(tripCount.value, previousTrips.value.length));
const distanceTrend = computed(() => trendFor(thisWeekDistanceKm.value, prevWeekDistanceKm.value));
const alertTrend = computed(() => trendFor(alertCount.value, previousAlerts.value.length));
</script>

<style scoped>
.feed-summary-card {
    border: 1px solid rgba(var(--v-theme-primary), 0.2);
}
.stat-block {
    min-width: 60px;
}
</style>
